import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  MarkdownMessage,
  useAgentRuntime,
  useComposerDraft,
  type ToolRendererProps
} from '@multiplayer-app/ai-agent-react'
import { AgentToolCallStatus } from '@multiplayer-app/ai-agent-types'
import { detectHost, HOST_LABELS } from '@/shared/office/host'
import { describeOfficeError } from '@/shared/office/errors'
import {
  applyOfficeToolLocally,
  canApplyOfficeTool,
  canScrollToToolTarget,
  onOfficeDocumentChanged,
  scrollToOfficeToolTarget
} from '@/shared/agent/tools'
import { findMessageIdForToolCall, sendToolApproval } from '@/shared/agent/toolApproval'
import { clip, getToolOutputLastAction, timeAgo } from './format'

/** Action name persisted on the tool call when the user applies a proposal. */
const TOOL_ACTION_APPLY = 'apply'

/** One-line summary of where the proposal lands (the content renders below it). */
function describeTarget(name: string, input: Record<string, unknown>): string | null {
  if (name === 'replaceWordText') {
    const find = typeof input.find === 'string' ? input.find : ''
    const findEnd = typeof input.findEnd === 'string' ? input.findEnd : ''
    const paragraph =
      typeof input.paragraphIndex === 'number' && Number.isInteger(input.paragraphIndex)
        ? ` in paragraph ${input.paragraphIndex}`
        : ''
    return findEnd
      ? `Replaces "${clip(find, 60)}" … "${clip(findEnd, 60)}"${paragraph} with:`
      : `Replaces "${clip(find, 60)}"${paragraph} with:`
  }
  if (name === 'replaceWordParagraphs') return `Replaces paragraphs ${input.start}–${input.end} with:`
  if (name === 'replaceWordDocument') return 'Replaces the entire document with:'
  if (name === 'replaceWordSelection') return 'Replaces the current selection with:'
  if (name === 'appendWordText') return 'Appends to the end of the document:'
  if (name === 'applyWordEdits') {
    const count = batchEdits(input).length
    // While the call streams in, `edits` may not have arrived yet.
    if (count === 0) return 'Preparing changes…'
    return `Applies ${count} change${count === 1 ? '' : 's'} in one batch`
  }
  return null
}

function batchEdits(input: Record<string, unknown>): Array<Record<string, unknown>> {
  return Array.isArray(input.edits) ? (input.edits as Array<Record<string, unknown>>) : []
}

/** Per-edit target line inside a batch card. */
function describeBatchEdit(edit: Record<string, unknown>): string {
  const text = typeof edit.text === 'string' ? edit.text : ''
  const deletes = !text.trim()
  if (edit.kind === 'replaceText') {
    const find = typeof edit.find === 'string' ? edit.find : ''
    return deletes
      ? `Deletes "${clip(find, 60)}" in paragraph ${edit.paragraphIndex}.`
      : `Replaces "${clip(find, 60)}" in paragraph ${edit.paragraphIndex} with:`
  }
  if (edit.kind === 'replaceParagraphs') {
    return deletes
      ? `Deletes paragraphs ${edit.start}–${edit.end}.`
      : `Replaces paragraphs ${edit.start}–${edit.end} with:`
  }
  if (edit.kind === 'insertBefore') return `Inserts before paragraph ${edit.paragraphIndex}:`
  return 'Appends to the end of the document:'
}

function describeSafetyNote(name: string, input: Record<string, unknown>): string | null {
  if (name === 'replaceWordDocument') return 'Review carefully: this will replace the entire document.'
  if (name === 'applyWordEdits') {
    return input.asTrackedChanges === false
      ? 'All edits are validated against the live document, then applied together.'
      : 'Edits land as tracked revisions (Track Changes) — accept or reject each one in Word.'
  }
  if (name === 'insertWordText' && typeof input.expectedSelectionText === 'string') {
    return 'Apply will fail safely if the insertion selection changed.'
  }
  if (name === 'replaceWordSelection') {
    return typeof input.expectedSelectionText === 'string'
      ? 'Apply will fail safely if the selection changed.'
      : 'Applies to the current Word selection at the time you click Apply.'
  }
  if (name === 'replaceWordParagraphs') {
    return typeof input.expectedStartText === 'string' || typeof input.expectedEndText === 'string'
      ? 'Apply will fail safely if the target paragraphs changed.'
      : 'Uses paragraph indexes from the document snapshot.'
  }
  return null
}

/** Proposal body: markdown-rendered content, or a textual summary for Excel. */
function ProposalPreview({
  name,
  input,
  onLocate
}: {
  name: string
  input: Record<string, unknown>
  onLocate?: () => void
}) {
  if (name === 'writeExcelRange') {
    const address = typeof input.address === 'string' ? input.address : 'A1'
    const rows = Array.isArray(input.values) ? input.values.length : 0
    return (
      <div className='ramp-proposal-summary'>
        Write {rows} row(s) starting at {address}
      </div>
    )
  }
  const target = describeTarget(name, input)
  const safetyNote = describeSafetyNote(name, input)
  const text = typeof input.text === 'string' ? input.text : ''
  return (
    <>
      {onLocate ? (
        // The target note doubles as a "jump to this spot" link. When a tool has
        // no note (e.g. insert), fall back to a generic locate label.
        <button type='button' className='ramp-proposal-note ramp-proposal-locate' onClick={onLocate}>
          {target ?? 'This change'}
        </button>
      ) : (
        target && <div className='ramp-proposal-note'>{target}</div>
      )}
      {safetyNote && <div className='ramp-proposal-note'>{safetyNote}</div>}
      {name === 'applyWordEdits' ? (
        batchEdits(input).map((edit, index) => {
          const editText = typeof edit.text === 'string' ? edit.text : ''
          return (
            <div key={index} className='ramp-proposal-batch-edit'>
              <div className='ramp-proposal-note'>
                {index + 1}. {describeBatchEdit(edit)}
              </div>
              {editText.trim() && (
                <div className='ramp-proposal-preview'>
                  <MarkdownMessage text={clip(editText, 2000)} />
                </div>
              )}
            </div>
          )
        })
      ) : (
        <div className='ramp-proposal-preview'>
          <MarkdownMessage text={clip(text, 4000)} />
        </div>
      )}
    </>
  )
}

/** Free-text result sent to the model when the user applies a proposal. */
const applyResponse = (hostLabel: string) => `Applied the proposed change to the ${hostLabel} document.`
/** Batch variant: reports how the edits landed (tracked revisions or direct). */
const batchApplyResponse = (count: number, tracked: boolean) =>
  `Applied all ${count} proposed change(s) to the Word document` +
  (tracked
    ? ' as tracked revisions (Track Changes); the user will accept or reject each one in Word.'
    : '. (Change tracking is unavailable in this Word host, so they were applied directly.)')
/** Result sent when the user skips a proposal without applying it. */
const SKIP_RESPONSE =
  'The user skipped this proposed change without applying it. Continue with the next actionable item, ' +
  'or stop and briefly summarize what still needs attention if none remain.'

/**
 * Renders an "apply" card for an Office write tool call from the `office` agent.
 *
 * Write tools are approval-gated (needsApproval), so a proposal arrives as a
 * PAUSED tool call carrying an `approvalId` and `approved === undefined`.
 * Applying runs the matching local Office.js handler directly (NOT
 * `runtime.tools.invoke`, which records a brand-new tool call and duplicates this
 * card) and then answers the approval (`approved: true`) via `sendToolApproval`,
 * which resumes the same turn so the model can propose the next fix with no
 * visible "continue" message. Skipping / regenerating answers with
 * `approved: false` and a reason. The recorded `approved` flag keeps the card in
 * its final state across reloads and tabs.
 *
 * Legacy tool calls with no `approvalId` (older chats, or ToolShelf invokes that
 * already ran locally) fall back to the previous `recordToolCallAction` apply.
 */
export function OfficeApplyRenderer({ call, chatId }: ToolRendererProps) {
  const runtime = useAgentRuntime()
  const { setValue: setComposerValue } = useComposerDraft()
  const [state, setState] = useState<'idle' | 'applying' | 'applied' | 'error' | 'declining' | 'declined'>('idle')
  const [error, setError] = useState<string>('')
  // Set once we've confirmed the proposal's anchor no longer matches the live
  // document. We only ever flip this on (never speculatively off), so the Apply
  // button is shown by default and removed only when the change is truly stale.
  const [isStale, setIsStale] = useState(false)
  const [isUnsafe, setIsUnsafe] = useState(false)
  const [isAmbiguous, setIsAmbiguous] = useState(false)
  // A batch apply that failed after some edits landed (see PartialBatchError).
  const [isPartial, setIsPartial] = useState(false)

  const input = (call.input ?? {}) as Record<string, unknown>
  const output = (call.output ?? {}) as Record<string, unknown>
  const host = detectHost()
  const hostLabel = HOST_LABELS[host]

  // Approval-gated proposals carry an approvalId; answering resumes the turn.
  const approvalId = call.approvalId
  const isApprovalCall = Boolean(approvalId)

  const lastAction = useMemo(() => getToolOutputLastAction(call.output), [call.output])
  // Applied when: the approval was answered yes (the server also stamps the
  // applied receipt output); the server recorded the legacy action; or
  // (ToolShelf path) the local handler ran at invoke time and stamped its output.
  const applied =
    state === 'applied' ||
    call.approved === true ||
    output.applied === true ||
    lastAction.action === TOOL_ACTION_APPLY ||
    output.inserted === true ||
    output.replaced === true
  // Declined when the user skipped/regenerated (approval answered no).
  const declined = state === 'declined' || call.approved === false
  // Withdrawn: the call FAILED before ever pausing for approval (its input was
  // rejected, e.g. an anchor over the 255-char schema limit) and the model saw
  // the error and re-proposed in the same turn. It is a discarded draft, not a
  // proposal: offering Apply for it lets the user apply BOTH the draft and the
  // corrected card, double-editing the target and turning the real card stale.
  const withdrawn = !declined && call.approved === undefined && (call.status === AgentToolCallStatus.Failed || Boolean(call.error))
  // Preparing: the model is still STREAMING this tool call's input, so the card
  // may render with a partial payload (a batch shows "0 changes" until `edits`
  // arrives). The approvalId is stamped only after the full input parsed and the
  // turn paused, and legacy/ToolShelf calls already carry an output — so a call
  // with neither, still running, is not yet ready to apply.
  const preparing =
    !isApprovalCall &&
    call.output === undefined &&
    !call.error &&
    (call.status === AgentToolCallStatus.Running || call.status === AgentToolCallStatus.Pending)
  const actionable = !applied && !declined && !withdrawn && !preparing
  const busy = state === 'applying' || state === 'declining'
  const appliedAgo = state === 'applied' && !lastAction.at ? 'just now' : timeAgo(lastAction.at)

  // Re-validate against the latest call without re-subscribing on every render
  // (call.input is a fresh object each render).
  const callRef = useRef(call)
  callRef.current = call
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const revalidate = useCallback(async () => {
    const current = callRef.current
    const status = await canApplyOfficeTool(current.name, (current.input ?? {}) as Record<string, unknown>)
    if (mountedRef.current) {
      setIsStale(status === 'stale')
      setIsUnsafe(status === 'unsafe')
      setIsAmbiguous(status === 'ambiguous')
    }
  }, [])

  // Check applicability on mount and whenever the document changes, so a stale
  // proposal drops its Apply button proactively. Skipped once the proposal is
  // resolved (applied/declined) and for non-Word hosts (no change events).
  useEffect(() => {
    if (!actionable || host !== 'word') {
      setIsStale(false)
      setIsUnsafe(false)
      setIsAmbiguous(false)
      return
    }
    void revalidate()
    return onOfficeDocumentChanged(host, () => void revalidate())
  }, [actionable, host, call.id, revalidate])

  // Legacy apply (no approvalId): persist an apply action + system message so the
  // card stays "Applied" across reloads. Superseded by the approval flow below.
  const persistAppliedAction = useCallback(async () => {
    if (!chatId || !runtime.transport?.recordToolCallAction) return
    await runtime.transport.recordToolCallAction(
      chatId,
      {
        toolCallId: call.id,
        action: TOOL_ACTION_APPLY,
        systemMessage: applyResponse(hostLabel)
      },
      // Include this client so the card updates immediately from the socket.
      { excludeSocketId: false }
    )
  }, [chatId, runtime.transport, call.id, hostLabel])

  // Answer this proposal's approval and resume the turn (no visible user message).
  const answerApproval = useCallback(
    async (approved: boolean, userResponse: string) => {
      if (!chatId || !approvalId) return
      const messageId = findMessageIdForToolCall(runtime, chatId, call.id)
      if (!messageId) throw new Error('Could not locate the message for this proposal.')
      await sendToolApproval(runtime, { chatId, messageId, approvalId, approved, userResponse })
    },
    [chatId, approvalId, runtime, call.id]
  )

  const apply = useCallback(async () => {
    setState('applying')
    setError('')
    let result: Record<string, unknown> | void
    try {
      result = await applyOfficeToolLocally(call.name, input)
    } catch (e) {
      setState('error')
      setError(describeOfficeError(e, 'Failed to apply the change to the document'))
      // A batch that failed after some edits landed must NOT offer Apply again
      // (retrying would re-apply the edits that already succeeded). Sticky —
      // unlike isStale it survives revalidation, which could otherwise re-enable
      // Apply because tracked revisions keep the original anchor text resolvable.
      if (e instanceof Error && e.name === 'PartialBatchError') {
        setIsPartial(true)
        return
      }
      // The document likely changed between our last check and this click;
      // re-check so the card settles into the stale state instead of the error.
      await revalidate()
      return
    }
    // The document is now changed; resuming the turn is best-effort from here.
    setState('applied')
    try {
      const response =
        call.name === 'applyWordEdits'
          ? batchApplyResponse(batchEdits(input).length, result?.tracked !== false)
          : applyResponse(hostLabel)
      if (isApprovalCall) await answerApproval(true, response)
      else await persistAppliedAction()
    } catch (e) {
      // Non-fatal: the change landed. Only the automatic continuation was missed.
      console.error('Applied the Office change but could not resume the agent turn:', e)
    }
  }, [call.name, input, isApprovalCall, answerApproval, hostLabel, persistAppliedAction, revalidate])

  // Decline the proposal (skip / regenerate). Only meaningful for approval calls;
  // resumes the turn with a reason so the model continues or proposes afresh.
  const decline = useCallback(
    async (userResponse: string) => {
      setState('declining')
      setError('')
      try {
        await answerApproval(false, userResponse)
        setState('declined')
      } catch (e) {
        setState('idle')
        setError(describeOfficeError(e, 'Could not update this proposal'))
      }
    },
    [answerApproval]
  )

  // Ask for a replacement proposal when the current one can no longer apply.
  // Approval calls decline with the reason (the model regenerates invisibly);
  // legacy calls fall back to seeding the composer for the user to send.
  const requestFreshProposal = useCallback(() => {
    const target = describeTarget(call.name, input)
    const what = target ? ` (the change that ${target.replace(/:$/, '').toLowerCase()})` : ''
    const reason = isPartial
      ? 'was only PARTIALLY applied: some of its edits are already in the document and the rest failed'
      : isUnsafe
        ? 'crosses Word table-cell boundaries and cannot preserve the document safely'
        : isAmbiguous
          ? 'matches more than one location and needs a paragraph-scoped target'
          : 'is out of date because the document changed'
    const instruction = isPartial
      ? 'Please re-read the affected paragraphs and propose a fresh batch containing ONLY the edits that are still missing'
      : isAmbiguous
        ? 'Please read the exact intended paragraph and propose a paragraph-scoped replacement'
        : 'Please review the current document and propose a smaller safe change'
    const message =
      `Your last proposed ${hostLabel} change ${reason}. ${instruction}${what}. ` +
      'If the current document already contains this change (e.g. an applied change covered it), ' +
      'say so briefly instead of proposing it again.'
    if (isApprovalCall) void decline(message)
    else setComposerValue(message)
  }, [call.name, input, hostLabel, isPartial, isUnsafe, isAmbiguous, isApprovalCall, decline, setComposerValue])

  const locate = useCallback(async () => {
    setError('')
    try {
      await scrollToOfficeToolTarget(call.name, input, applied)
    } catch (e) {
      setState('error')
      setError(describeOfficeError(e, 'Could not locate this text in the document'))
    }
  }, [call.name, input, applied])

  // Before apply we locate the target; after apply we locate the inserted
  // content. A stale target no longer exists to locate, so drop the link.
  const canLocate = canScrollToToolTarget(call.name, applied) && (applied || (!isStale && !isAmbiguous))
  // A stale proposal is no longer actionable: its anchor changed, so the apply
  // error is suppressed in favour of a plain "out of date" note.
  const showPartial = actionable && isPartial
  const showStale = actionable && isStale && !isPartial
  const showUnsafe = actionable && isUnsafe && !isPartial
  const showAmbiguous = actionable && isAmbiguous && !isPartial
  const showApplyRow = actionable && !showPartial && !showStale && !showUnsafe && !showAmbiguous

  return (
    <div className='ramp-proposal'>
      <div className='ramp-proposal-title'>Proposed {hostLabel} change</div>
      <ProposalPreview name={call.name} input={input} onLocate={canLocate ? locate : undefined} />
      {state === 'error' && !showStale && !showUnsafe && !showAmbiguous && (
        <div className='ramp-proposal-error'>{error}</div>
      )}
      <div className='ramp-proposal-footer'>
        {applied && <span className='ramp-proposal-applied'>Applied ✓{appliedAgo ? ` · ${appliedAgo}` : ''}</span>}
        {declined && <span className='ramp-proposal-skipped'>Skipped</span>}
        {withdrawn && !applied && (
          <span className='ramp-proposal-skipped'>Withdrawn — this draft was invalid and was replaced by a corrected proposal.</span>
        )}
        {preparing && <span className='ramp-proposal-skipped'>Preparing this proposal…</span>}
        {showPartial && (
          <div className='ramp-proposal-stale'>
            <span>Partially applied — some edits landed before one failed. The rest need a fresh proposal.</span>
            <button type='button' className='ramp-proposal-refresh' onClick={requestFreshProposal} disabled={busy}>
              Ask for a fresh proposal
            </button>
          </div>
        )}
        {showStale && (
          <div className='ramp-proposal-stale'>
            <span>Out of date — the {hostLabel} document changed since this was suggested.</span>
            <button type='button' className='ramp-proposal-refresh' onClick={requestFreshProposal} disabled={busy}>
              Ask for a fresh proposal
            </button>
          </div>
        )}
        {showUnsafe && (
          <div className='ramp-proposal-stale'>
            <span>This proposal crosses Word table-cell boundaries and cannot preserve the document safely.</span>
            <button type='button' className='ramp-proposal-refresh' onClick={requestFreshProposal} disabled={busy}>
              Ask for a safer proposal
            </button>
          </div>
        )}
        {showAmbiguous && (
          <div className='ramp-proposal-stale'>
            <span>This wording appears in multiple places. A paragraph-scoped proposal is required.</span>
            <button type='button' className='ramp-proposal-refresh' onClick={requestFreshProposal} disabled={busy}>
              Ask for a precise proposal
            </button>
          </div>
        )}
        {showApplyRow && (
          <>
            <button type='button' className='ramp-proposal-apply' onClick={apply} disabled={busy}>
              {state === 'applying' ? 'Applying…' : 'Apply to document'}
            </button>
            {isApprovalCall && (
              <button
                type='button'
                className='ramp-proposal-skip'
                onClick={() => void decline(SKIP_RESPONSE)}
                disabled={busy}
              >
                {state === 'declining' ? 'Skipping…' : 'Skip'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
